import { type FormEvent, type ReactNode, useEffect, useState } from 'react'
import { KeyRound, ShieldCheck } from 'lucide-react'
import { api, AUTH_REQUIRED_EVENT, post } from '../api'
import { Button } from './UI'

type AuthStatus = {
  enabled: boolean
  authenticated: boolean
  session_hours: number
  mfa_pending: boolean
  mfa_enrolled: boolean
}
type Setup = { secret: string; qr_data: string }
type LoginResult = Partial<AuthStatus> & { recovery_codes?: string[] }

export default function AuthGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus | null>(null)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [setup, setSetup] = useState<Setup | null>(null)
  const [recovery, setRecovery] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    const refresh = () => api<AuthStatus>('/api/auth/status')
      .then(value => { if (active) setStatus(value) })
      .catch(() => { if (active) setError('Non riesco a verificare lo stato di accesso.') })
    window.addEventListener(AUTH_REQUIRED_EVENT, refresh)
    void refresh()
    return () => { active = false; window.removeEventListener(AUTH_REQUIRED_EVENT, refresh) }
  }, [])

  useEffect(() => {
    let active = true
    if (status?.mfa_pending && !status.mfa_enrolled && !status.authenticated) {
      api<Setup>('/api/auth/mfa/setup').then(value => { if (active) setSetup(value) })
        .catch(reason => { if (active) setError(reason.message || 'Configurazione non disponibile.') })
    } else setSetup(null)
    return () => { active = false }
  }, [status?.mfa_pending, status?.mfa_enrolled, status?.authenticated])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true); setError('')
    try {
      const next = status?.mfa_pending
        ? await post<LoginResult>('/api/auth/mfa/verify', { code })
        : await post<LoginResult>('/api/auth/login', { password })
      setPassword(''); setCode('')
      if (next.recovery_codes?.length) setRecovery(next.recovery_codes)
      setStatus(current => current ? { ...current, ...next,
        mfa_pending: next.authenticated ? false : !!next.mfa_pending,
        mfa_enrolled: next.authenticated ? true : !!next.mfa_enrolled } : current)
    } catch (reason: any) { setError(reason.message || 'Accesso non riuscito.') }
    finally { setBusy(false) }
  }

  const back = async () => {
    setBusy(true)
    try { await post('/api/auth/logout', {}) } catch { /* Local state also resets if the request expires. */ }
    setStatus(current => current ? { ...current, authenticated: false, mfa_pending: false } : current)
    setSetup(null); setPassword(''); setCode(''); setError(''); setBusy(false)
  }

  const downloadRecovery = () => {
    const blob = new Blob(['Investing Lab — codici di recupero monouso\n\n' + recovery.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url; link.download = 'investing-lab-codici-recupero.txt'; link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  if (recovery.length) return <main className="auth-screen"><section className="auth-card">
    <ShieldCheck size={25} /><h1>Autenticatore configurato</h1>
    <p>Conserva questi codici in un luogo sicuro. Ogni codice funziona una volta e permette di accedere insieme alla password se perdi l’autenticatore. Li mostriamo soltanto ora.</p>
    <ul>{recovery.map(value => <li key={value}><code>{value}</code></li>)}</ul>
    <Button onClick={downloadRecovery}>Scarica i codici</Button>
    <Button variant="primary" onClick={() => setRecovery([])}>Ho conservato i codici: continua</Button>
  </section></main>
  if (error && !status) return <main className="auth-screen"><section className="auth-card"><h1>Accesso non verificabile</h1><p>{error}</p></section></main>
  if (!status) return <main className="auth-screen"><div className="route-loading">Verifico l’accesso…</div></main>
  if (!status.enabled || status.authenticated) return <>{children}</>

  return <main className="auth-screen"><section className="auth-card">
    <div className="auth-mark"><ShieldCheck size={25} /></div><span className="eyebrow">Area privata</span>
    <h1>{status.mfa_pending ? status.mfa_enrolled ? 'Verifica con l’autenticatore' : 'Configura l’autenticatore' : 'Investing Lab'}</h1>
    {!status.mfa_pending && <p>Accedi con password e autenticatore. La sessione dura {status.session_hours} {status.session_hours === 1 ? 'ora' : 'ore'}.</p>}
    {status.mfa_pending && !status.mfa_enrolled && <>
      <p>Aggiungi un account nell’app di autenticazione e scansiona il QR. Poi inserisci il codice a sei cifre per completare la configurazione.</p>
      {setup ? <><img src={setup.qr_data} width={220} height={220} alt="QR per configurare il tuo autenticatore" />
        <details><summary>Non riesci a scansionarlo?</summary><p>Inserisci manualmente questa chiave e scegli un codice basato sul tempo:</p><code style={{ overflowWrap: 'anywhere' }}>{setup.secret}</code></details></>
        : <p>Preparo la configurazione…</p>}
    </>}
    {status.mfa_pending && status.mfa_enrolled && <p>Inserisci il codice dell’app di autenticazione oppure un codice di recupero monouso.</p>}
    <form onSubmit={submit} className="auth-form">
      {status.mfa_pending
        ? <div className="field"><label htmlFor="access-otp">Codice di verifica</label><input id="access-otp" autoComplete="one-time-code" maxLength={64} autoFocus value={code} onChange={event => setCode(event.target.value)} /></div>
        : <div className="field"><label htmlFor="access-password">Password</label><input id="access-password" type="password" autoComplete="current-password" autoFocus value={password} onChange={event => setPassword(event.target.value)} /></div>}
      {error && <div className="notice warn" role="alert">{error}</div>}
      <Button type="submit" variant="primary" disabled={busy || (status.mfa_pending ? code.length < 6 : !password)}><KeyRound size={15} />{busy ? 'Verifica…' : status.mfa_pending ? 'Verifica codice' : 'Continua'}</Button>
      {status.mfa_pending && <Button type="button" disabled={busy} onClick={back}>Torna alla password</Button>}
    </form>
  </section></main>
}
